/**
 * ItemsView (ITEMS-01) — the pricebook screen Seth asked for: search a 3,500-item
 * catalog, filter by category, edit a single price inline, or multi-select and
 * bulk-edit (set a flat price, bump by a %, activate/deactivate). The friendly
 * replacement for editing ESC's giant item grid.
 */
import { useState } from "react";
import { Search, Package, Plus, Pencil, Sparkles, ExternalLink, X, ImageOff, ArrowRight } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import { SlideOver } from "@/components/ui/SlideOver";
import {
  useItems,
  useItemCategories,
  useManufacturers,
  useSaveItem,
  useBulkSetPrice,
  useBulkPriceAdjust,
  useBulkPriceAddDollar,
  useBulkSetActive,
  useVendors,
  usePricebookFlags,
  useResolvePricebookFlag,
  useKeeperDigest,
  fetchItemById,
  type Item,
  type PricebookFlag,
} from "./items";

// Equipment types for the catalog filter (the GBB-quotable kinds).
const EQUIP_TYPES = ["Furnace", "Air Conditioner", "Heat Pump", "Water Heater", "Accessory"];
import { ItemEditorModal } from "./ItemEditorModal";
import { PageHeader } from "@/components/ui/PageHeader";
import { CopyCode } from "@/components/Copyable";

const money = (n: number | null) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

export function ItemsView() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [manufacturer, setManufacturer] = useState<string | null>(null);
  const [equipType, setEquipType] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const { data: items, isLoading } = useItems(search, category, showArchived, showInactive, manufacturer, equipType);
  const { data: cats } = useItemCategories();
  const { data: manus } = useManufacturers();
  const { data: vendors } = useVendors();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Editor: undefined = closed, null = new item, Item = editing that row.
  const [editing, setEditing] = useState<Item | null | undefined>(undefined);
  // PB-ENRICH-01: the AI Keeper drawer (last night's work + open flags).
  const [keeperOpen, setKeeperOpen] = useState(false);
  const { data: flags } = usePricebookFlags();
  const openFlags = (flags ?? []).length;
  const openItemById = async (id: string) => {
    const it = await fetchItemById(id);
    if (it) { setKeeperOpen(false); setEditing(it); }
  };

  const list = items ?? [];
  const allSelected = list.length > 0 && list.every((i) => selected.has(i.id));
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(list.map((i) => i.id)));
  const toggle = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const ids = [...selected];

  return (
    <div className="mx-auto max-w-7xl p-6">
      <PageHeader
        icon={Package}
        title="Pricebook"
        description="Parts & materials — search, filter, and adjust flat-rate pricing one at a time or in bulk."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setKeeperOpen(true)} title="What the AI keeper filled or fixed overnight, and what it left for you">
              <Sparkles className="h-4 w-4 text-violet-500" /> AI Keeper
              {openFlags > 0 && (
                <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-[11px] font-bold text-amber-700">{openFlags}</span>
              )}
            </Button>
            <Button onClick={() => setEditing(null)}><Plus className="h-4 w-4" /> Add item</Button>
          </div>
        }
      />
      {keeperOpen && <KeeperPanel onClose={() => setKeeperOpen(false)} onOpenItem={openItemById} />}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search part #, model, size or brand"
            className="w-full rounded-md border border-hairline py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>
        <select
          value={category ?? ""}
          onChange={(e) => setCategory(e.target.value || null)}
          className="rounded-md border border-hairline px-2 py-2 text-sm"
        >
          <option value="">All categories</option>
          {(cats ?? []).map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={equipType ?? ""}
          onChange={(e) => setEquipType(e.target.value || null)}
          title="Equipment type (for GBB pricing)"
          className="rounded-md border border-hairline px-2 py-2 text-sm"
        >
          <option value="">All types</option>
          {EQUIP_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          value={manufacturer ?? ""}
          onChange={(e) => setManufacturer(e.target.value || null)}
          title="Manufacturer (Lennox shows only Lennox)"
          className="rounded-md border border-hairline px-2 py-2 text-sm"
        >
          <option value="">All brands</option>
          {(manus ?? []).map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600"
          />
          Show inactive
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600"
          />
          Show archived
        </label>
        <span className="text-xs text-slate-400">{list.length} shown</span>
      </div>

      {ids.length > 0 && (
        <BulkBar ids={ids} onClear={() => setSelected(new Set())} />
      )}

      <div className="overflow-x-auto rounded-xl border border-hairline bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hairline bg-slate-50/70 text-left text-[12px] font-semibold text-slate-500">
              <th className="w-8 px-2 py-2">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all" />
              </th>
              <th className="px-2 py-2">Item</th>
              <th className="px-2 py-2">Description</th>
              <th className="px-2 py-2">Category</th>
              <th className="px-2 py-2 text-right">Cost</th>
              <th className="px-2 py-2 text-right">Price</th>
              <th className="px-2 py-2 text-right">Retail</th>
              <th className="px-2 py-2 text-right">Stock</th>
              <th className="px-2 py-2">Vendor</th>
              <th className="w-8 px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-sm text-slate-400">Loading…</td></tr>
            )}
            {!isLoading && list.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-sm text-slate-400">No items match.</td></tr>
            )}
            {list.map((it) => (
              <ItemRow
                key={it.id}
                it={it}
                checked={selected.has(it.id)}
                onToggle={() => toggle(it.id)}
                onEdit={() => setEditing(it)}
                vendorLabel={vendors?.label}
              />
            ))}
          </tbody>
        </table>
      </div>

      {editing !== undefined && (
        <ItemEditorModal
          item={editing}
          open
          onClose={() => setEditing(undefined)}
        />
      )}
    </div>
  );
}

function ItemRow({
  it,
  checked,
  onToggle,
  onEdit,
  vendorLabel,
}: {
  it: Item;
  checked: boolean;
  onToggle: () => void;
  onEdit: () => void;
  vendorLabel?: (code: string | null) => string;
}) {
  const save = useSaveItem();
  const [price, setPrice] = useState(it.base_price == null ? "" : String(it.base_price));
  const dirty = price !== (it.base_price == null ? "" : String(it.base_price));
  const commit = () => {
    if (!dirty) return;
    save.mutate({ id: it.id, base_price: price === "" ? null : Number(price) });
  };
  // Whole row opens the editor; the checkbox + inline price stop propagation so
  // they keep working without triggering the slide-out.
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return (
    <tr
      onClick={onEdit}
      className={cn(
        "group cursor-pointer border-b border-hairline last:border-0 hover:bg-slate-50",
        it.inactive && "opacity-50",
      )}
    >
      <td className="px-2 py-1.5" onClick={stop}>
        <input type="checkbox" checked={checked} onChange={onToggle} aria-label={`Select ${it.part_number}`} />
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 font-medium text-slate-800">
        <CopyCode value={it.part_number} label="Copy part number" variant="text" size="md" mono={false} className="font-medium group-hover:underline" />
        {it.inactive && <span className="ml-1 text-[11px] text-slate-400">(inactive)</span>}
        {it.archived && <span className="ml-1 text-[11px] text-rose-400">(archived)</span>}
      </td>
      <td className="max-w-[280px] truncate px-2 py-1.5 text-slate-600" title={it.description ?? ""}>
        {it.description}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 text-slate-500">{it.category}</td>
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{money(it.last_purchase)}</td>
      <td className="px-2 py-1.5 text-right" onClick={stop}>
        <span className="inline-flex items-center gap-1">
          <span className="text-slate-400">$</span>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            onBlur={commit}
            onClick={stop}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
            className={cn(
              "w-20 rounded-md border px-1.5 py-0.5 text-right text-sm tabular-nums",
              dirty ? "border-brand-400 bg-brand-50" : "border-hairline",
            )}
          />
        </span>
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{money(it.sell_price)}</td>
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{it.qty_in_stock ?? "—"}</td>
      <td className="max-w-[140px] truncate px-2 py-1.5 text-slate-500" title={it.vendor ?? ""}>
        {vendorLabel ? vendorLabel(it.vendor) : it.vendor}
      </td>
      <td className="px-2 py-1.5 text-right">
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${it.part_number}`}
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-brand-700"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

function BulkBar({ ids, onClear }: { ids: string[]; onClear: () => void }) {
  const setPrice = useBulkSetPrice();
  const adjust = useBulkPriceAdjust();
  const setActive = useBulkSetActive();
  const addDollar = useBulkPriceAddDollar();
  const [field, setField] = useState<"base_price" | "sell_price">("base_price");
  const [flat, setFlat] = useState("");
  const [pct, setPct] = useState("");
  const [dol, setDol] = useState("");
  const busy = setPrice.isPending || adjust.isPending || addDollar.isPending || setActive.isPending;
  const done = () => onClear();

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-brand-200 bg-brand-50/60 px-3 py-2 text-sm">
      <span className="font-semibold text-brand-800">{ids.length} selected</span>
      <span className="text-slate-300">·</span>

      <div className="inline-flex overflow-hidden rounded-md border border-hairline" title="Which price these bulk edits change">
        {(([["base_price", "Cost"], ["sell_price", "Retail"]]) as const).map(([f, label]) => (
          <button
            key={f}
            type="button"
            onClick={() => setField(f)}
            className={cn(
              "px-2 py-1 text-xs font-semibold transition-colors",
              field === f ? "bg-brand-700 text-white" : "bg-white text-slate-500 hover:bg-slate-50",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <span className="text-slate-300">·</span>

      <span className="inline-flex items-center gap-1">
        <span className="text-xs text-slate-500">Set $</span>
        <input type="number" value={flat} onChange={(e) => setFlat(e.target.value)} className="w-20 rounded-md border border-hairline px-1.5 py-1 text-right text-sm" />
        <button
          type="button"
          disabled={busy || flat === ""}
          onClick={() => setPrice.mutate({ ids, price: Number(flat), field }, { onSuccess: done })}
          className="rounded-md bg-brand-700 px-2 py-1 text-xs font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300"
        >
          Set
        </button>
      </span>
      <span className="text-slate-300">·</span>

      <span className="inline-flex items-center gap-1">
        <span className="text-xs text-slate-500">Adjust</span>
        <input type="number" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="%" className="w-14 rounded-md border border-hairline px-1.5 py-1 text-right text-sm" />
        <button
          type="button"
          disabled={busy || pct === ""}
          onClick={() => adjust.mutate({ ids, pct: Math.abs(Number(pct)), field }, { onSuccess: done })}
          className="rounded-md border border-hairline bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          +%
        </button>
        <button
          type="button"
          disabled={busy || pct === ""}
          onClick={() => adjust.mutate({ ids, pct: -Math.abs(Number(pct)), field }, { onSuccess: done })}
          className="rounded-md border border-hairline bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          −%
        </button>
      </span>
      <span className="text-slate-300">·</span>

      <span className="inline-flex items-center gap-1">
        <span className="text-xs text-slate-500">Adjust $</span>
        <input type="number" value={dol} onChange={(e) => setDol(e.target.value)} placeholder="$" className="w-16 rounded-md border border-hairline px-1.5 py-1 text-right text-sm" />
        <button
          type="button"
          disabled={busy || dol === ""}
          onClick={() => addDollar.mutate({ ids, amount: Math.abs(Number(dol)), field }, { onSuccess: done })}
          className="rounded-md border border-hairline bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          +$
        </button>
        <button
          type="button"
          disabled={busy || dol === ""}
          onClick={() => addDollar.mutate({ ids, amount: -Math.abs(Number(dol)), field }, { onSuccess: done })}
          className="rounded-md border border-hairline bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          −$
        </button>
      </span>
      <span className="text-slate-300">·</span>

      <button type="button" disabled={busy} onClick={() => setActive.mutate({ ids, inactive: false }, { onSuccess: done })} className="rounded-md border border-hairline bg-white px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50">
        Activate
      </button>
      <button type="button" disabled={busy} onClick={() => setActive.mutate({ ids, inactive: true }, { onSuccess: done })} className="rounded-md border border-hairline bg-white px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100">
        Deactivate
      </button>

      <button type="button" onClick={onClear} className="ml-auto text-xs font-medium text-slate-400 hover:text-slate-600">
        Clear
      </button>
    </div>
  );
}

/* ── PB-ENRICH-01: the AI Keeper drawer — what the nightly caretaker filled or
   fixed (receipts from pegasus_pricebook_ai_log) and what it LEFT FOR A HUMAN
   (pegasus_pricebook_flag). The keeper's law, visible: it fills blanks and
   heals provably-dead photo links; anything else becomes a flag here. ────── */
function KeeperPanel({ onClose, onOpenItem }: { onClose: () => void; onOpenItem: (itemId: string) => void }) {
  const { data: flags, isLoading: flagsLoading } = usePricebookFlags();
  const { data: log } = useKeeperDigest();
  const resolve = useResolvePricebookFlag();

  const rows = log ?? [];
  const dress = rows.filter((r) => r.kind === "dress" && (r.wrote_features || r.wrote_overview)).length;
  const fills = rows.filter((r) => r.kind === "keeper_fill");
  const dead = rows.filter((r) => r.kind === "image_dead").length;
  const healed = rows.filter((r) => r.kind === "image_heal").length;
  const lastAt = rows[0]?.created_at ? new Date(rows[0].created_at) : null;

  const KIND_CHIP: Record<string, { label: string; cls: string }> = {
    dead_image: { label: "Photo link died", cls: "bg-amber-100 text-amber-700" },
    no_image: { label: "No photo found", cls: "bg-slate-100 text-slate-600" },
    missing_field: { label: "Couldn't identify", cls: "bg-rose-50 text-rose-700" },
  };
  const applyLabel = (f: PricebookFlag): string | null => {
    if (f.suggestion?.image_url) return "Use found photo";
    if (f.kind === "missing_field" && f.suggestion?.source_url) return "Save product link";
    return null;
  };

  return (
    <SlideOver open onClose={onClose} size="md" title="AI Keeper"
      subtitle="Fills blanks and fixes dead photo links overnight — never overwrites your work. Everything else lands here for you.">
      <div className="space-y-5">
        <section>
          <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Last 26 hours</h3>
          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">Quiet night — nothing needed filling. The keeper runs around 4:20 AM.</p>
          ) : (
            <div className="rounded-lg border border-hairline bg-slate-50/60 p-3 text-sm text-slate-700">
              <span className="font-semibold">{dress}</span> homeowner-copy fill{dress === 1 ? "" : "s"} ·{" "}
              <span className="font-semibold">{fills.length}</span> catalog fact{fills.length === 1 ? "" : "s"} filled ·{" "}
              <span className="font-semibold">{dead}</span> dead photo link{dead === 1 ? "" : "s"} found
              {dead > 0 && <> (<span className="font-semibold text-emerald-700">{healed}</span> healed automatically)</>}
              {lastAt && (
                <span className="mt-1 block text-[11px] text-slate-400">
                  Latest activity {lastAt.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </span>
              )}
            </div>
          )}
          {fills.length > 0 && (
            <ul className="mt-2 space-y-1">
              {fills.slice(0, 8).map((r) => (
                <li key={r.id} className="flex items-center gap-2 text-xs text-slate-600">
                  <Sparkles className="h-3 w-3 shrink-0 text-violet-400" />
                  <span className="font-mono font-semibold">{r.part_number}</span>
                  <span className="min-w-0 flex-1 truncate">{r.field} → {r.new_value}</span>
                  {r.source_url && (
                    <a href={r.source_url} target="_blank" rel="noreferrer" title="Source receipt" className="shrink-0 text-slate-300 hover:text-brand-600">
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Needs a human {flags && flags.length > 0 ? `· ${flags.length}` : ""}
          </h3>
          {flagsLoading ? (
            <div className="h-16 animate-pulse rounded-lg bg-slate-50" />
          ) : (flags ?? []).length === 0 ? (
            <p className="rounded-lg border border-dashed border-hairline px-3 py-4 text-center text-sm text-slate-400">
              Nothing waiting — the keeper handled everything it was allowed to.
            </p>
          ) : (
            <ul className="space-y-2">
              {(flags ?? []).map((f) => {
                const chip = KIND_CHIP[f.kind] ?? { label: f.kind, cls: "bg-slate-100 text-slate-600" };
                const apl = applyLabel(f);
                return (
                  <li key={f.id} className="rounded-xl border border-hairline bg-white p-3 shadow-e1">
                    <div className="flex items-center gap-2">
                      <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold", chip.cls)}>{chip.label}</span>
                      <span className="font-mono text-xs font-semibold text-slate-700">{f.part_number}</span>
                      <span className="ml-auto text-[11px] text-slate-400">
                        {new Date(f.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                    </div>
                    {f.reason && <p className="mt-1.5 text-xs leading-snug text-slate-600">{f.reason}</p>}
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {apl && (
                        <button type="button" disabled={resolve.isPending}
                          onClick={() => resolve.mutate({ flag: f, action: "apply" })}
                          className="rounded-full bg-brand-700 px-2.5 py-1 text-[11px] font-bold text-white transition-colors hover:bg-brand-800 disabled:opacity-50">
                          {apl}
                        </button>
                      )}
                      <button type="button" onClick={() => void onOpenItem(f.item_id)}
                        className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700 transition-colors hover:bg-slate-200">
                        Open item <ArrowRight className="h-3 w-3" />
                      </button>
                      {f.suggestion?.source_url && (
                        <a href={f.suggestion.source_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700 transition-colors hover:bg-slate-200">
                          Source <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      <button type="button" disabled={resolve.isPending} title="Dismiss — it won't come back for this item"
                        onClick={() => resolve.mutate({ flag: f, action: "dismiss" })}
                        className="ml-auto rounded-full p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {(flags ?? []).some((f) => f.kind === "dead_image") && (
            <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-slate-400">
              <ImageOff className="mt-0.5 h-3 w-3 shrink-0" />
              A dead photo link is cleared right away so proposals show the clean tile instead of a broken image —
              the flag is just the reminder to find a new photo.
            </p>
          )}
        </section>
      </div>
    </SlideOver>
  );
}
